/**
 * Hook for monitoring session expiry and providing warnings
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api'

interface SessionWarning {
  show: boolean
  timeRemaining: number
  message: string
}

export function useSessionChecker() {
  const { user, logout } = useAuth()
  const [warning, setWarning] = useState<SessionWarning>({
    show: false,
    timeRemaining: 0,
    message: ''
  })

  const warningTimerRef = useRef<number | null>(null)
  const checkTimerRef = useRef<number | null>(null)
  const isExtendingRef = useRef<boolean>(false)

  const getSessionExpiry = useCallback(() => {
    const expiryString = localStorage.getItem('session_expiry')
    return expiryString ? parseInt(expiryString, 10) : null
  }, [])

  const getRemainingTime = useCallback(() => {
    const expiry = getSessionExpiry()
    if (!expiry) return 0
    return Math.max(0, Math.floor((expiry - Date.now()) / 1000))
  }, [getSessionExpiry])

  const extendSession = useCallback(async () => {
    // Prevent multiple simultaneous extend attempts
    if (isExtendingRef.current) return
    isExtendingRef.current = true

    try {
      // Use the refresh token to get a new access token
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        console.warn('No refresh token available, cannot extend session')
        logout()
        return
      }

      const response = await apiClient.post<{
        access_token: string
        token_type: string
        expires_in: number
        refresh_expires_in?: number
      }>('/auth/refresh', { refresh_token: refreshToken })

      // Update the access token
      localStorage.setItem('access_token', response.access_token)

      // Note: We don't update session_expiry here because the refresh token itself
      // hasn't changed. The session_expiry tracks the refresh token expiry.
      // If the backend returns refresh_expires_in in the future, we could update it here.

      // Hide warning and restart monitoring
      setWarning({ show: false, timeRemaining: 0, message: '' })

      // Clear existing timers and restart
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current)
      }
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current)
      }

      startSessionMonitoring()
    } catch (error) {
      console.error('Failed to extend session:', error)
      // If refresh fails, log out the user
      logout()
    } finally {
      isExtendingRef.current = false
    }
  }, [logout])

  const startWarning = useCallback((timeRemaining: number) => {
    setWarning({
      show: true,
      timeRemaining,
      message: 'Your session is about to expire'
    })

    // Update warning every second
    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current)
    }
    
    warningTimerRef.current = setInterval(() => {
      const remaining = getRemainingTime()
      if (remaining <= 0) {
        // Session expired - logout
        logout()
      } else {
        setWarning(prev => ({
          ...prev,
          timeRemaining: remaining
        }))
      }
    }, 1000)
  }, [getRemainingTime, logout])

  const checkSession = useCallback(() => {
    if (!user) return

    const remaining = getRemainingTime()

    if (remaining <= 0) {
      // Session expired - logout immediately
      logout()
      return
    }

    // Show warning when less than 5 minutes remain
    if (remaining <= 5 * 60 && !warning.show) {
      startWarning(remaining)
    }
  }, [user, getRemainingTime, logout, warning.show, startWarning])

  const startSessionMonitoring = useCallback(() => {
    if (!user) return

    // Initial check
    checkSession()

    // Check every 30 seconds
    if (checkTimerRef.current) {
      clearInterval(checkTimerRef.current)
    }
    
    checkTimerRef.current = setInterval(checkSession, 30 * 1000)
  }, [user, checkSession])

  useEffect(() => {
    if (!user) {
      setWarning({ show: false, timeRemaining: 0, message: '' })

      // Clear timers
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current)
        warningTimerRef.current = null
      }
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current)
        checkTimerRef.current = null
      }
      return
    }

    // Check if we have session expiry data
    const expiry = getSessionExpiry()
    if (!expiry) {
      // No session expiry data - try to refresh the token to establish one
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        // Attempt to refresh to set proper session expiry
        extendSession()
      } else {
        // No refresh token and no session expiry - create a temporary expiry
        // based on the default refresh token lifetime (7 days or 90 days with remember me)
        const rememberMe = localStorage.getItem('remember_me') === 'true'
        const sessionDuration = rememberMe
          ? 90 * 24 * 60 * 60 * 1000  // 90 days in milliseconds
          : 7 * 24 * 60 * 60 * 1000   // 7 days in milliseconds
        const expirationTime = Date.now() + sessionDuration
        localStorage.setItem('session_expiry', expirationTime.toString())
      }
    }

    startSessionMonitoring()

    // Cleanup on unmount
    return () => {
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current)
      }
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current)
      }
    }
  }, [user, getSessionExpiry, startSessionMonitoring, extendSession])

  const dismissWarning = useCallback(() => {
    setWarning(prev => ({ ...prev, show: false }))
    
    // Clear the warning timer
    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current)
      warningTimerRef.current = null
    }
  }, [])

  return {
    warning,
    extendSession,
    checkSession,
    dismissWarning
  }
}
